import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Buildings, MapPin, Phone, Envelope, IdentificationCard,
  PencilSimple, Trash, Upload
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

export function SettingsLogoSection({ logoSrc, uploading, fileInputRef, onLogoUpload, onDeleteLogo }) {
  return (
    <Card className="chantix-card" data-testid="logo-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Buildings size={20} className="text-[#FF6B00]" />
          Logo de l'entreprise
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="w-32 h-32 rounded-xl border-2 border-dashed border-[#E5E7EB] bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0"
            data-testid="logo-preview">
            {logoSrc ? (
              <img src={logoSrc} alt="Logo entreprise" className="w-full h-full object-contain p-2" data-testid="logo-image" />
            ) : (
              <div className="text-center text-[#4B5563]">
                <Buildings size={32} className="mx-auto mb-1 opacity-40" />
                <p className="text-xs">Aucun logo</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 flex-1 w-full sm:w-auto">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml"
              className="hidden" onChange={onLogoUpload} data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="h-12 w-full border-[#0055FF] text-[#0055FF] hover:bg-blue-50" data-testid="upload-logo-btn">
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload size={18} className="mr-2" />}
              {uploading ? 'Upload en cours...' : 'Charger un logo'}
            </Button>
            {logoSrc && (
              <Button variant="outline" onClick={onDeleteLogo}
                className="h-12 w-full border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-logo-btn">
                <Trash size={18} className="mr-2" /> Supprimér le logo
              </Button>
            )}
            <p className="text-xs text-[#4B5563]">JPEG, PNG, WebP ou SVG. Max 5 Mo.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function SettingsCompanySection({ settings, onChange }) {
  return (
    <Card className="chantix-card" data-testid="company-info-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Buildings size={20} className="text-[#0055FF]" />
          Informations de l'entreprise
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5"><Buildings size={14} className="text-[#4B5563]" />Raison sociale</Label>
          <Input value={settings.company_name} onChange={e => onChange('company_name', e.target.value)}
            placeholder="Ex: BTP Solutions SAS" className="h-12" data-testid="company-name-input" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5"><MapPin size={14} className="text-[#4B5563]" />Adresse</Label>
          <Input value={settings.address} onChange={e => onChange('address', e.target.value)}
            placeholder="Ex: 12 rue de la Construction, 75001 Paris" className="h-12" data-testid="company-address-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5"><Phone size={14} className="text-[#4B5563]" />Telephone</Label>
            <Input value={settings.phone} onChange={e => onChange('phone', e.target.value)}
              placeholder="Ex: 01 23 45 67 89" className="h-12" data-testid="company-phone-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5"><Envelope size={14} className="text-[#4B5563]" />Email</Label>
            <Input type="email" value={settings.email} onChange={e => onChange('email', e.target.value)}
              placeholder="Ex: contact@btpsolutions.fr" className="h-12" data-testid="company-email-input" />
          </div>
        </div>
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5"><IdentificationCard size={14} className="text-[#4B5563]" />SIRET</Label>
          <Input value={settings.siret} onChange={e => onChange('siret', e.target.value)}
            placeholder="Ex: 123 456 789 00012" className="h-12" data-testid="company-siret-input" />
        </div>
      </CardContent>
    </Card>
  );
}

export function SettingsSignatureSection({ settings, onChange }) {
  return (
    <Card className="chantix-card" data-testid="signature-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <PencilSimple size={20} className="text-[#FF6B00]" />
          Signature des rapports
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#4B5563]">Ces informations apparaitront en bas des rapports générés.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5">Nom du signataire</Label>
            <Input value={settings.signatory_name} onChange={e => onChange('signatory_name', e.target.value)}
              placeholder="Ex: Jean Dupont" className="h-12" data-testid="signatory-name-input" />
          </div>
          <div>
            <Label className="mb-1.5">Fonction</Label>
            <Input value={settings.signatory_role} onChange={e => onChange('signatory_role', e.target.value)}
              placeholder="Ex: Chef de projet OPC" className="h-12" data-testid="signatory-role-input" />
          </div>
        </div>
        {(settings.signatory_name || settings.signatory_role) && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-[#E5E7EB]" data-testid="signature-preview">
            <p className="text-xs text-[#4B5563] mb-2 uppercase tracking-wider font-semibold">Aperçu signature</p>
            <Separator className="mb-3" />
            <p className="font-semibold text-[#111827]">{settings.signatory_name || '-'}</p>
            <p className="text-sm text-[#4B5563]">{settings.signatory_role || '-'}</p>
            {settings.company_name && <p className="text-sm text-[#4B5563]">{settings.company_name}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
